import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import FormattingUtils from 'shared/FormattingUtils'
import FleetLogoCircleColor from 'core-system/FleetLogos/FleetLogoCircleColor'

const CardContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background-color: ${(props) => props.theme.palette.grey.grey200};
  border-radius: 0.5rem;
`

const CardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const CardIcon = styled.div<{ bgColor: string }>`
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  background-color: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
`

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const BalanceSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.125rem;
`

const ReloadLink = styled.span`
  color: ${(props) => props.theme.palette.primary.pink500};
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`

// Clipper card icon
const ClipperCardIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect x='2' y='5' width='20' height='14' rx='2' fill='#94254F' />
    <rect x='2' y='9' width='20' height='3' fill='#57112C' />
  </svg>
)

// Fleet card icon using the Fleet logo
const FleetCardIcon = () => <FleetLogoCircleColor width={24} height={24} />

export type CardType = 'clipper' | 'fleet'

interface WalletCardProps {
  name: string
  lastFour?: string
  balance: number
  cardType?: CardType
  iconColor?: string
  onReload?: () => void
  showReload?: boolean
}

const WalletCard: React.FC<WalletCardProps> = ({
  name,
  lastFour,
  balance,
  cardType = 'clipper',
  iconColor,
  onReload,
  showReload = true,
}) => {
  const bgColor =
    iconColor || (cardType === 'fleet' ? 'transparent' : '#FCE9ED')
  const CardIconComponent =
    cardType === 'fleet' ? FleetCardIcon : ClipperCardIcon

  return (
    <CardContainer>
      <CardInfo>
        <CardIcon bgColor={bgColor}>
          <CardIconComponent />
        </CardIcon>
        <CardDetails>
          <Text variant='action4'>{name}</Text>
          {lastFour && (
            <Text variant='caption' textColor='placeholder'>
              **** {lastFour}
            </Text>
          )}
        </CardDetails>
      </CardInfo>
      <BalanceSection>
        <Text variant='action3'>{FormattingUtils.formatDollar(balance)}</Text>
        {showReload && onReload && (
          <ReloadLink onClick={onReload}>Reload</ReloadLink>
        )}
      </BalanceSection>
    </CardContainer>
  )
}

export default React.memo(WalletCard)
